/**
 * invalidationBus — a tiny, typed, synchronous pub/sub used to keep list views
 * fresh after a mutation (UX 16.2: "created but not showing").
 *
 * The problem it solves: a list composable fetches once on mount and then has
 * no idea that a *sibling* component (a create/edit dialog, a row action) just
 * changed the underlying data. Rather than wiring prop drilling or a heavy
 * store dependency between unrelated features, the mutator simply announces
 * "this domain changed" and any interested list re-fetches itself.
 *
 * Design notes:
 *   - Keys are a closed union ({@link InvalidationKey}) so a typo (`'event'`
 *     vs `'events'`) is a compile error, not a silent no-op.
 *   - Dispatch is **synchronous**: by the time {@link publish} returns, every
 *     subscriber for that key has run. Callers that want to await the resulting
 *     re-fetch should do so inside their own subscriber.
 *   - No leaks: {@link subscribe} returns an unsubscribe function, and the
 *     internal set for a key is dropped once its last subscriber leaves.
 *   - Re-entrancy safe: the fan-out iterates a snapshot, so a subscriber that
 *     subscribes/unsubscribes (its own or a sibling) during dispatch never
 *     corrupts the in-flight delivery.
 *
 * @example List composable: subscribe on mount, clean up on unmount.
 * ```ts
 * import { onMounted, onUnmounted, ref } from 'vue';
 * import { subscribe } from '@/shared/lib/invalidationBus';
 *
 * export function useEventsList() {
 *   const items = ref<Event[]>([]);
 *   const load = async () => { items.value = await api.fetchEvents(); };
 *
 *   let stop: (() => void) | undefined;
 *   onMounted(() => {
 *     void load();
 *     stop = subscribe('events', load); // re-fetch whenever events change
 *   });
 *   onUnmounted(() => stop?.());          // no leak: drop the subscription
 *
 *   return { items, reload: load };
 * }
 * ```
 *
 * @example Mutation: publish after a successful write.
 * ```ts
 * import { publish } from '@/shared/lib/invalidationBus';
 *
 * async function createEvent(payload: NewEvent) {
 *   await api.createEvent(payload);
 *   publish('events'); // every mounted events list re-fetches synchronously
 * }
 * ```
 */

/**
 * The closed set of cache-domain keys callers may publish/subscribe to. Extend
 * this union (not a free `string`) when a new invalidatable list appears, so
 * every call site stays type-checked.
 */
export type InvalidationKey = 'events' | 'consultations' | 'notifications' | 'bookings' | 'calendar';

/** A subscriber callback. Invoked once per matching {@link publish}. */
export type InvalidationCallback = () => void;

/** Tear-down handle returned by {@link subscribe}; calling it stops delivery. */
export type Unsubscribe = () => void;

/**
 * Per-key registries of subscribers. A key is absent from the map until it has
 * at least one subscriber, and is removed again once its last subscriber
 * unsubscribes — so the bus holds no memory for idle domains.
 */
const registry = new Map<InvalidationKey, Set<InvalidationCallback>>();

/**
 * Register `callback` to be invoked whenever `key` is published.
 *
 * @param key - The domain to listen on.
 * @param callback - Invoked synchronously on each matching {@link publish}.
 * @returns An idempotent unsubscribe function. Calling it more than once, or
 *          after the key has otherwise emptied, is safe and has no effect.
 */
export function subscribe(key: InvalidationKey, callback: InvalidationCallback): Unsubscribe {
    let subscribers = registry.get(key);
    if (!subscribers) {
        subscribers = new Set<InvalidationCallback>();
        registry.set(key, subscribers);
    }
    subscribers.add(callback);

    let active = true;
    return () => {
        if (!active) {
            return;
        }
        active = false;
        const current = registry.get(key);
        if (!current) {
            return;
        }
        current.delete(callback);
        if (current.size === 0) {
            registry.delete(key);
        }
    };
}

/**
 * Synchronously notify every subscriber of `key`. Returns once they have all
 * run. A key with no subscribers is a silent no-op.
 *
 * The fan-out iterates a snapshot of the subscriber set, so subscribing or
 * unsubscribing from within a callback affects only *future* publishes, never
 * the one in flight.
 *
 * @param key - The domain that changed and whose lists should refresh.
 */
export function publish(key: InvalidationKey): void {
    const subscribers = registry.get(key);
    if (!subscribers || subscribers.size === 0) {
        return;
    }
    // Snapshot first: a callback may mutate `subscribers` mid-iteration.
    for (const callback of [...subscribers]) {
        callback();
    }
}
